import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalculatorIcon,
  BanknotesIcon,
  HomeIcon,
  AcademicCapIcon,
  ScaleIcon,
  ChartBarIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

type CalculatorType =
  | 'compound-interest'
  | 'loan-payment'
  | 'retirement'
  | 'emergency-fund'
  | 'debt-payoff'
  | 'investment-growth'
  | 'currency-exchange';

interface CalculatorResult {
  title: string;
  value: string;
  subtitle?: string;
  details?: { label: string; value: string }[];
}

export const FinancialCalculators: React.FC = () => {
  const { t } = useTranslation();
  const [activeCalculator, setActiveCalculator] = useState<CalculatorType>('compound-interest');
  const [result, setResult] = useState<CalculatorResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // Calculator inputs
  const [compoundInputs, setCompoundInputs] = useState({
    principal: 10000,
    rate: 7,
    years: 10,
    frequency: 'yearly'
  });

  const [loanInputs, setLoanInputs] = useState({
    principal: 250000,
    rate: 6.5,
    years: 30
  });

  const [retirementInputs, setRetirementInputs] = useState({
    currentAge: 30,
    retirementAge: 65,
    currentSavings: 50000,
    monthlyContribution: 500,
    expectedReturn: 7
  });

  const [emergencyInputs, setEmergencyInputs] = useState({
    monthlyExpenses: 4000,
    months: 6
  });

  const [debtInputs, setDebtInputs] = useState({
    balance: 20000,
    rate: 18,
    payment: 500
  });

  const [investmentInputs, setInvestmentInputs] = useState({
    initialInvestment: 5000,
    monthlyContribution: 200,
    years: 20,
    expectedReturn: 8
  });

  const [currencyInputs, setCurrencyInputs] = useState({
    amount: 100,
    fromCurrency: 'USD',
    toCurrency: 'EUR'
  });

  // Common currencies for the dropdown
  const currencies = [
    { code: 'USD', name: 'US Dollar' },
    { code: 'EUR', name: 'Euro' },
    { code: 'GBP', name: 'British Pound' },
    { code: 'JPY', name: 'Japanese Yen' },
    { code: 'CHF', name: 'Swiss Franc' },
    { code: 'CAD', name: 'Canadian Dollar' },
    { code: 'AUD', name: 'Australian Dollar' },
    { code: 'NZD', name: 'New Zealand Dollar' },
    { code: 'CNY', name: 'Chinese Yuan' },
    { code: 'INR', name: 'Indian Rupee' },
    { code: 'MXN', name: 'Mexican Peso' },
    { code: 'BRL', name: 'Brazilian Real' },
    { code: 'KRW', name: 'South Korean Won' },
    { code: 'SGD', name: 'Singapore Dollar' },
    { code: 'HKD', name: 'Hong Kong Dollar' },
    { code: 'SEK', name: 'Swedish Krona' },
    { code: 'NOK', name: 'Norwegian Krone' },
    { code: 'DKK', name: 'Danish Krone' },
    { code: 'PLN', name: 'Polish Zloty' },
    { code: 'ZAR', name: 'South African Rand' },
  ];

  const calculators = [
    { id: 'compound-interest' as const, icon: ChartBarIcon, label: t('calculators:types.compoundInterest', 'Compound Interest') },
    { id: 'loan-payment' as const, icon: HomeIcon, label: t('calculators:types.loanPayment', 'Loan Payment') },
    { id: 'retirement' as const, icon: BanknotesIcon, label: t('calculators:types.retirement', 'Retirement Savings') },
    { id: 'emergency-fund' as const, icon: ScaleIcon, label: t('calculators:types.emergencyFund', 'Emergency Fund') },
    { id: 'debt-payoff' as const, icon: ArrowPathIcon, label: t('calculators:types.debtPayoff', 'Debt Payoff') },
    { id: 'investment-growth' as const, icon: CurrencyDollarIcon, label: t('calculators:types.investmentGrowth', 'Investment Growth') },
    { id: 'currency-exchange' as const, icon: GlobeAltIcon, label: t('calculators:types.currencyExchange', 'Currency Exchange') },
  ];

  const formatCurrency = (value: number) => {
    // Round to nearest dollar (no cents) for cleaner display
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.round(value));
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(value * 100) / 100);
  };

  const calculateCompoundInterest = () => {
    setIsCalculating(true);
    setTimeout(() => {
      const { principal, rate, years, frequency } = compoundInputs;
      const r = rate / 100;
      let n = 1;
      if (frequency === 'monthly') n = 12;
      else if (frequency === 'quarterly') n = 4;
      else if (frequency === 'daily') n = 365;

      const futureValue = principal * Math.pow(1 + r / n, n * years);
      const totalInterest = futureValue - principal;

      setResult({
        title: t('calculators:results.futureValue', 'Future Value'),
        value: formatCurrency(futureValue),
        subtitle: t('calculators:results.afterYears', { years, defaultValue: `After ${years} years` }),
        details: [
          { label: t('calculators:results.principal', 'Principal'), value: formatCurrency(principal) },
          { label: t('calculators:results.totalInterest', 'Total Interest Earned'), value: formatCurrency(totalInterest) },
          { label: t('calculators:results.effectiveRate', 'Effective Annual Rate'), value: `${formatNumber((Math.pow(1 + r / n, n) - 1) * 100)}%` },
        ]
      });
      setIsCalculating(false);
    }, 300);
  };

  const calculateLoanPayment = () => {
    setIsCalculating(true);
    setTimeout(() => {
      const { principal, rate, years } = loanInputs;
      const monthlyRate = rate / 100 / 12;
      const numPayments = years * 12;

      const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
      const totalPayment = monthlyPayment * numPayments;
      const totalInterest = totalPayment - principal;

      setResult({
        title: t('calculators:results.monthlyPayment', 'Monthly Payment'),
        value: formatCurrency(monthlyPayment),
        subtitle: t('calculators:results.loanTerm', { years, defaultValue: `${years}-year loan` }),
        details: [
          { label: t('calculators:results.loanAmount', 'Loan Amount'), value: formatCurrency(principal) },
          { label: t('calculators:results.totalInterest', 'Total Interest'), value: formatCurrency(totalInterest) },
          { label: t('calculators:results.totalPayment', 'Total Payment'), value: formatCurrency(totalPayment) },
          { label: t('calculators:results.payoffDate', 'Payoff Date'), value: new Date(Date.now() + years * 365 * 24 * 60 * 60 * 1000).toLocaleDateString() },
        ]
      });
      setIsCalculating(false);
    }, 300);
  };

  const calculateRetirement = () => {
    setIsCalculating(true);
    setTimeout(() => {
      const { currentAge, retirementAge, currentSavings, monthlyContribution, expectedReturn } = retirementInputs;
      const years = retirementAge - currentAge;
      const monthlyRate = expectedReturn / 100 / 12;
      const numMonths = years * 12;

      // Future value of current savings
      const fvCurrentSavings = currentSavings * Math.pow(1 + monthlyRate, numMonths);

      // Future value of monthly contributions
      const fvContributions = monthlyContribution * ((Math.pow(1 + monthlyRate, numMonths) - 1) / monthlyRate);

      const totalSavings = fvCurrentSavings + fvContributions;
      const totalContributions = currentSavings + (monthlyContribution * numMonths);
      const totalEarnings = totalSavings - totalContributions;

      // Estimate monthly retirement income (4% rule)
      const monthlyIncome = (totalSavings * 0.04) / 12;

      setResult({
        title: t('calculators:results.retirementSavings', 'Retirement Savings'),
        value: formatCurrency(totalSavings),
        subtitle: t('calculators:results.atAge', { age: retirementAge, defaultValue: `At age ${retirementAge}` }),
        details: [
          { label: t('calculators:results.yearsToRetire', 'Years Until Retirement'), value: `${years}` },
          { label: t('calculators:results.totalContributions', 'Total Contributions'), value: formatCurrency(totalContributions) },
          { label: t('calculators:results.investmentEarnings', 'Investment Earnings'), value: formatCurrency(totalEarnings) },
          { label: t('calculators:results.estimatedMonthlyIncome', 'Estimated Monthly Income (4% Rule)'), value: formatCurrency(monthlyIncome) },
        ]
      });
      setIsCalculating(false);
    }, 300);
  };

  const calculateEmergencyFund = () => {
    setIsCalculating(true);
    setTimeout(() => {
      const { monthlyExpenses, months } = emergencyInputs;
      const targetFund = monthlyExpenses * months;

      setResult({
        title: t('calculators:results.emergencyFundTarget', 'Emergency Fund Target'),
        value: formatCurrency(targetFund),
        subtitle: t('calculators:results.monthsOfExpenses', { months, defaultValue: `${months} months of expenses` }),
        details: [
          { label: t('calculators:results.monthlyExpenses', 'Monthly Expenses'), value: formatCurrency(monthlyExpenses) },
          { label: t('calculators:results.coveragePeriod', 'Coverage Period'), value: `${months} months` },
          { label: t('calculators:results.weeklyTarget', 'Weekly Savings to Reach in 1 Year'), value: formatCurrency(targetFund / 52) },
          { label: t('calculators:results.monthlyTarget', 'Monthly Savings to Reach in 1 Year'), value: formatCurrency(targetFund / 12) },
        ]
      });
      setIsCalculating(false);
    }, 300);
  };

  const calculateDebtPayoff = () => {
    setIsCalculating(true);
    setTimeout(() => {
      const { balance, rate, payment } = debtInputs;
      const monthlyRate = rate / 100 / 12;

      // Calculate months to payoff
      const monthsToPayoff = Math.ceil(
        Math.log(payment / (payment - balance * monthlyRate)) / Math.log(1 + monthlyRate)
      );

      const totalPayment = payment * monthsToPayoff;
      const totalInterest = totalPayment - balance;
      const yearsToPayoff = Math.floor(monthsToPayoff / 12);
      const remainingMonths = monthsToPayoff % 12;

      setResult({
        title: t('calculators:results.debtFreeDate', 'Debt-Free Timeline'),
        value: `${yearsToPayoff} years, ${remainingMonths} months`,
        subtitle: t('calculators:results.paymentAmount', { amount: formatCurrency(payment), defaultValue: `Paying ${formatCurrency(payment)}/month` }),
        details: [
          { label: t('calculators:results.currentBalance', 'Current Balance'), value: formatCurrency(balance) },
          { label: t('calculators:results.totalInterest', 'Total Interest'), value: formatCurrency(totalInterest) },
          { label: t('calculators:results.totalPayment', 'Total Payment'), value: formatCurrency(totalPayment) },
          { label: t('calculators:results.payoffDate', 'Payoff Date'), value: new Date(Date.now() + monthsToPayoff * 30 * 24 * 60 * 60 * 1000).toLocaleDateString() },
        ]
      });
      setIsCalculating(false);
    }, 300);
  };

  const calculateInvestmentGrowth = () => {
    setIsCalculating(true);
    setTimeout(() => {
      const { initialInvestment, monthlyContribution, years, expectedReturn } = investmentInputs;
      const monthlyRate = expectedReturn / 100 / 12;
      const numMonths = years * 12;

      const fvInitial = initialInvestment * Math.pow(1 + monthlyRate, numMonths);
      const fvContributions = monthlyContribution * ((Math.pow(1 + monthlyRate, numMonths) - 1) / monthlyRate);

      const totalValue = fvInitial + fvContributions;
      const totalContributions = initialInvestment + (monthlyContribution * numMonths);
      const totalEarnings = totalValue - totalContributions;

      setResult({
        title: t('calculators:results.portfolioValue', 'Portfolio Value'),
        value: formatCurrency(totalValue),
        subtitle: t('calculators:results.afterYears', { years, defaultValue: `After ${years} years` }),
        details: [
          { label: t('calculators:results.initialInvestment', 'Initial Investment'), value: formatCurrency(initialInvestment) },
          { label: t('calculators:results.totalContributions', 'Total Contributions'), value: formatCurrency(totalContributions) },
          { label: t('calculators:results.investmentEarnings', 'Investment Earnings'), value: formatCurrency(totalEarnings) },
          { label: t('calculators:results.returnOnInvestment', 'Return on Investment'), value: `${formatNumber((totalEarnings / totalContributions) * 100)}%` },
        ]
      });
      setIsCalculating(false);
    }, 300);
  };

  const calculateCurrencyExchange = async () => {
    setIsCalculating(true);
    const { amount, fromCurrency, toCurrency } = currencyInputs;

    try {
      // Fetch real-time exchange rate from Frankfurter API
      const response = await fetch(
        `https://api.frankfurter.dev/v1/latest?base=${fromCurrency}&symbols=${toCurrency}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch exchange rate');
      }

      const data = await response.json();
      const exchangeRate = data.rates[toCurrency];
      const convertedAmount = amount * exchangeRate;

      // Format the converted amount based on the target currency
      const formatConvertedCurrency = (value: number, currency: string) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value);
      };

      const fromCurrencyName = currencies.find(c => c.code === fromCurrency)?.name || fromCurrency;
      const toCurrencyName = currencies.find(c => c.code === toCurrency)?.name || toCurrency;

      setResult({
        title: t('calculators:results.convertedAmount', 'Converted Amount'),
        value: formatConvertedCurrency(convertedAmount, toCurrency),
        subtitle: `${formatConvertedCurrency(amount, fromCurrency)} → ${toCurrencyName}`,
        details: [
          { label: t('calculators:results.originalAmount', 'Original Amount'), value: formatConvertedCurrency(amount, fromCurrency) },
          { label: t('calculators:results.exchangeRate', 'Exchange Rate'), value: `1 ${fromCurrency} = ${exchangeRate.toFixed(6)} ${toCurrency}` },
          { label: t('calculators:results.fromCurrency', 'From'), value: `${fromCurrency} (${fromCurrencyName})` },
          { label: t('calculators:results.toCurrency', 'To'), value: `${toCurrency} (${toCurrencyName})` },
          { label: t('calculators:results.rateDate', 'Rate Date'), value: data.date },
          { label: t('calculators:results.dataSource', 'Data Source'), value: 'European Central Bank' },
        ]
      });
    } catch (error) {
      console.error('Currency conversion error:', error);
      setResult({
        title: t('calculators:results.error', 'Error'),
        value: t('calculators:results.conversionFailed', 'Conversion Failed'),
        subtitle: t('calculators:results.tryAgain', 'Please try again'),
        details: [
          { label: t('calculators:results.errorMessage', 'Error'), value: 'Could not fetch exchange rate. Check your internet connection.' },
        ]
      });
    } finally {
      setIsCalculating(false);
    }
  };

  const handleCalculate = () => {
    switch (activeCalculator) {
      case 'compound-interest':
        calculateCompoundInterest();
        break;
      case 'loan-payment':
        calculateLoanPayment();
        break;
      case 'retirement':
        calculateRetirement();
        break;
      case 'emergency-fund':
        calculateEmergencyFund();
        break;
      case 'debt-payoff':
        calculateDebtPayoff();
        break;
      case 'investment-growth':
        calculateInvestmentGrowth();
        break;
      case 'currency-exchange':
        calculateCurrencyExchange();
        break;
    }
  };

  const renderCalculatorInputs = () => {
    switch (activeCalculator) {
      case 'compound-interest':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.principal', 'Principal Amount')}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                <input
                  type="number"
                  value={compoundInputs.principal}
                  onChange={(e) => setCompoundInputs({ ...compoundInputs, principal: Number(e.target.value) })}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.annualRate', 'Annual Interest Rate (%)')}
              </label>
              <input
                type="number"
                step="0.1"
                value={compoundInputs.rate}
                onChange={(e) => setCompoundInputs({ ...compoundInputs, rate: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.years', 'Number of Years')}
              </label>
              <input
                type="number"
                value={compoundInputs.years}
                onChange={(e) => setCompoundInputs({ ...compoundInputs, years: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.compoundingFrequency', 'Compounding Frequency')}
              </label>
              <select
                value={compoundInputs.frequency}
                onChange={(e) => setCompoundInputs({ ...compoundInputs, frequency: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              >
                <option value="yearly">{t('calculators:frequency.yearly', 'Yearly')}</option>
                <option value="quarterly">{t('calculators:frequency.quarterly', 'Quarterly')}</option>
                <option value="monthly">{t('calculators:frequency.monthly', 'Monthly')}</option>
                <option value="daily">{t('calculators:frequency.daily', 'Daily')}</option>
              </select>
            </div>
          </div>
        );

      case 'loan-payment':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.loanAmount', 'Loan Amount')}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                <input
                  type="number"
                  value={loanInputs.principal}
                  onChange={(e) => setLoanInputs({ ...loanInputs, principal: Number(e.target.value) })}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.interestRate', 'Interest Rate (%)')}
              </label>
              <input
                type="number"
                step="0.1"
                value={loanInputs.rate}
                onChange={(e) => setLoanInputs({ ...loanInputs, rate: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.loanTerm', 'Loan Term (Years)')}
              </label>
              <input
                type="number"
                value={loanInputs.years}
                onChange={(e) => setLoanInputs({ ...loanInputs, years: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>
        );

      case 'retirement':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('calculators:inputs.currentAge', 'Current Age')}
                </label>
                <input
                  type="number"
                  value={retirementInputs.currentAge}
                  onChange={(e) => setRetirementInputs({ ...retirementInputs, currentAge: Number(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('calculators:inputs.retirementAge', 'Retirement Age')}
                </label>
                <input
                  type="number"
                  value={retirementInputs.retirementAge}
                  onChange={(e) => setRetirementInputs({ ...retirementInputs, retirementAge: Number(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.currentSavings', 'Current Savings')}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                <input
                  type="number"
                  value={retirementInputs.currentSavings}
                  onChange={(e) => setRetirementInputs({ ...retirementInputs, currentSavings: Number(e.target.value) })}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.monthlyContribution', 'Monthly Contribution')}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                <input
                  type="number"
                  value={retirementInputs.monthlyContribution}
                  onChange={(e) => setRetirementInputs({ ...retirementInputs, monthlyContribution: Number(e.target.value) })}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.expectedReturn', 'Expected Annual Return (%)')}
              </label>
              <input
                type="number"
                step="0.1"
                value={retirementInputs.expectedReturn}
                onChange={(e) => setRetirementInputs({ ...retirementInputs, expectedReturn: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>
        );

      case 'emergency-fund':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.monthlyExpenses', 'Monthly Expenses')}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                <input
                  type="number"
                  value={emergencyInputs.monthlyExpenses}
                  onChange={(e) => setEmergencyInputs({ ...emergencyInputs, monthlyExpenses: Number(e.target.value) })}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.coverageMonths', 'Months of Coverage')}
              </label>
              <select
                value={emergencyInputs.months}
                onChange={(e) => setEmergencyInputs({ ...emergencyInputs, months: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              >
                <option value={3}>3 months</option>
                <option value={6}>6 months (recommended)</option>
                <option value={9}>9 months</option>
                <option value={12}>12 months</option>
              </select>
            </div>
          </div>
        );

      case 'debt-payoff':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.currentBalance', 'Current Balance')}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                <input
                  type="number"
                  value={debtInputs.balance}
                  onChange={(e) => setDebtInputs({ ...debtInputs, balance: Number(e.target.value) })}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.interestRate', 'Interest Rate (%)')}
              </label>
              <input
                type="number"
                step="0.1"
                value={debtInputs.rate}
                onChange={(e) => setDebtInputs({ ...debtInputs, rate: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.monthlyPayment', 'Monthly Payment')}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                <input
                  type="number"
                  value={debtInputs.payment}
                  onChange={(e) => setDebtInputs({ ...debtInputs, payment: Number(e.target.value) })}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
          </div>
        );

      case 'investment-growth':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.initialInvestment', 'Initial Investment')}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                <input
                  type="number"
                  value={investmentInputs.initialInvestment}
                  onChange={(e) => setInvestmentInputs({ ...investmentInputs, initialInvestment: Number(e.target.value) })}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.monthlyContribution', 'Monthly Contribution')}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                <input
                  type="number"
                  value={investmentInputs.monthlyContribution}
                  onChange={(e) => setInvestmentInputs({ ...investmentInputs, monthlyContribution: Number(e.target.value) })}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.years', 'Investment Period (Years)')}
              </label>
              <input
                type="number"
                value={investmentInputs.years}
                onChange={(e) => setInvestmentInputs({ ...investmentInputs, years: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.expectedReturn', 'Expected Annual Return (%)')}
              </label>
              <input
                type="number"
                step="0.1"
                value={investmentInputs.expectedReturn}
                onChange={(e) => setInvestmentInputs({ ...investmentInputs, expectedReturn: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>
        );

      case 'currency-exchange':
        return (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-xs text-blue-700 dark:text-blue-300 flex items-center gap-2">
                <GlobeAltIcon className="h-4 w-4" />
                {t('calculators:currencyNote', 'Exchange rates are fetched in real-time from the European Central Bank via Frankfurter API.')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.amount', 'Amount')}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={currencyInputs.amount}
                onChange={(e) => setCurrencyInputs({ ...currencyInputs, amount: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.fromCurrency', 'From Currency')}
              </label>
              <select
                value={currencyInputs.fromCurrency}
                onChange={(e) => setCurrencyInputs({ ...currencyInputs, fromCurrency: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              >
                {currencies.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('calculators:inputs.toCurrency', 'To Currency')}
              </label>
              <select
                value={currencyInputs.toCurrency}
                onChange={(e) => setCurrencyInputs({ ...currencyInputs, toCurrency: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              >
                {currencies.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
            </div>
            {currencyInputs.fromCurrency === currencyInputs.toCurrency && (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                {t('calculators:sameCurrencyWarning', 'Please select different currencies to convert.')}
              </p>
            )}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <CalculatorIcon className="h-16 w-16 mx-auto mb-4 text-amber-600 dark:text-amber-400" />
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('calculators:title', 'Financial Calculators')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('calculators:subtitle', 'Plan your financial future with our suite of calculators')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Calculator Selection */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('calculators:selectCalculator', 'Select Calculator')}
              </h2>
              <div className="space-y-2">
                {calculators.map((calc) => {
                  const Icon = calc.icon;
                  return (
                    <button
                      key={calc.id}
                      onClick={() => {
                        setActiveCalculator(calc.id);
                        setResult(null);
                      }}
                      className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors ${
                        activeCalculator === calc.id
                          ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-l-4 border-amber-500'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <Icon className="h-5 w-5 mr-3" />
                      <span className="text-sm font-medium">{calc.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Calculator Input */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {calculators.find(c => c.id === activeCalculator)?.label}
              </h2>

              {renderCalculatorInputs()}

              <button
                onClick={handleCalculate}
                disabled={isCalculating}
                className="w-full mt-6 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {isCalculating ? t('calculators:calculating', 'Calculating...') : t('calculators:calculate', 'Calculate')}
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('calculators:results.title', 'Results')}
              </h2>

              {result ? (
                <div>
                  <div className="text-center mb-6 p-4 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
                    <p className="text-sm text-amber-600 dark:text-amber-400 mb-1">{result.title}</p>
                    <p className="font-bold text-amber-700 dark:text-amber-300 whitespace-nowrap"
                       style={{
                         fontSize: result.value.length > 18 ? '1.25rem' :
                                   result.value.length > 14 ? '1.5rem' :
                                   result.value.length > 10 ? '1.75rem' : '1.875rem'
                       }}>
                      {result.value}
                    </p>
                    {result.subtitle && (
                      <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">{result.subtitle}</p>
                    )}
                  </div>

                  {result.details && (
                    <div className="space-y-3">
                      {result.details.map((detail, index) => (
                        <div key={index} className="flex justify-between text-sm gap-2">
                          <span className="text-gray-600 dark:text-gray-400 min-w-0">{detail.label}</span>
                          <span className="font-medium text-gray-900 dark:text-white text-right whitespace-nowrap flex-shrink-0"
                                style={{
                                  fontSize: detail.value.length > 16 ? '0.75rem' : '0.875rem'
                                }}>
                            {detail.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <CalculatorIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>{t('calculators:enterValues', 'Enter values and click Calculate')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialCalculators;
